import { UnassignedItem } from 'src/entities/dispatch';

/** 未割当リストの id は `lr-{legRunId}`（toUnassignedItem / parseUnassignedIds と同じ規則） */
export function unassignedIdsOfLegRuns(legRunIds: readonly number[]): Set<string> {
  return new Set(legRunIds.map((id) => `lr-${id}`));
}

/**
 * 自動配車に送った未割当を隠す。
 *
 * 送った案件がリストに残ったままだと、計算中に「まだ未割当」なのか「もう割り付いた」のかが
 * 読めない。V3Plans は送った時点で消し、割り付かなかったものだけを戻す
 * （hooks/usePlanningRequest.ts:162 の addAllocatedOrderIds と Component.tsx:1187 の戻し）。
 *
 * 隠すものが無ければ同じ配列参照を返す（下流の memo を無駄に壊さない）。
 */
export function filterHiddenUnassigned(
  items: UnassignedItem[],
  hiddenIds: ReadonlySet<string>,
): UnassignedItem[] {
  if (hiddenIds.size === 0) return items;
  const filtered = items.filter((item) => !hiddenIds.has(item.id));
  return filtered.length === items.length ? items : filtered;
}

/**
 * 割り付いてリストから消えた id の追跡をやめる。
 * 残し続けると、同じ案件が未割当に戻ったとき（配車の取り消しなど）隠れたままになる。
 */
export function pruneHiddenUnassignedIds(
  hiddenIds: ReadonlySet<string>,
  items: readonly UnassignedItem[],
): ReadonlySet<string> {
  if (hiddenIds.size === 0) return hiddenIds;
  const alive = new Set(items.map((item) => item.id));
  const next = new Set([...hiddenIds].filter((id) => alive.has(id)));
  return next.size === hiddenIds.size ? hiddenIds : next;
}

/** 割り付かなかった案件を再表示する。選択は解除していないのでそのまま戻る */
export function revealUnassignedIds(
  hiddenIds: ReadonlySet<string>,
  legRunIds: readonly number[],
): ReadonlySet<string> {
  if (hiddenIds.size === 0 || legRunIds.length === 0) return hiddenIds;
  const next = new Set(hiddenIds);
  legRunIds.forEach((id) => next.delete(`lr-${id}`));
  return next.size === hiddenIds.size ? hiddenIds : next;
}
